type NativeImageSaveResult = {
  method?: string
  stage?: string
  duration_ms?: number
  bytes?: number
  http_status?: number
}

export type StoreProductType =
  | 'consumable'
  | 'non_consumable'
  | 'auto_renewable'
  | 'non_renewable'
  | 'unknown'

export type StoreProduct = {
  product_id: string
  display_price: string
  price: number
  currency_code: string
  product_type: StoreProductType
  subscription_period_unit?: 'day' | 'week' | 'month' | 'year' | 'unknown' | null
  subscription_period_value?: number | null
}

export type NativeIapTransaction = {
  receipt_data: string
  transaction_id: string
  product_id: string
  app_account_token?: string
}

export type NativePurchaseResult =
  | {
      status: 'verified'
      receipt_data: string
      transaction_id: string
      product_id: string
      app_account_token?: string
    }
  | { status: 'cancelled' }
  | { status: 'pending' }
  | { status: 'unverified'; stage: string; message?: string }
  | { status: 'error'; stage: string; message?: string }

export type NativeIapRecoveryBridge = {
  finishTransaction: (transactionId: string) => Promise<void>
  getUnfinishedTransactions: () => Promise<{
    transactions: NativeIapTransaction[]
  }>
}

export type NativeIapRestoreBridge = {
  restorePurchases: (syncAppStore?: boolean) => Promise<{
    transactions: NativeIapTransaction[]
    unverified_count: number
  }>
}

declare global {
  interface Window {
    __NATIVE_BRIDGE__?: {
      purchase: (
        productId: string,
        appAccountToken: string,
        expectedProductType?: StoreProductType,
      ) => Promise<NativePurchaseResult>
      finishTransaction?: NativeIapRecoveryBridge['finishTransaction']
      getUnfinishedTransactions?: NativeIapRecoveryBridge['getUnfinishedTransactions']
      restorePurchases?: NativeIapRestoreBridge['restorePurchases']
      addAppleIapTransactionListener?: (
        listener: (transaction: NativeIapTransaction) => void,
      ) => Promise<() => Promise<void>>
      getStoreProducts?: (productIds: string[]) => Promise<{
        products: StoreProduct[]
      }>
      getStorefront?: () => Promise<{ countryCode: string }>
      manageSubscriptions?: () => Promise<{
        status: 'presented' | 'unavailable' | 'error'
        message?: string
      }>
      saveImage?: (base64: string) => Promise<void>
      saveImageFromUrl?: (url: string) => Promise<NativeImageSaveResult | void>
      pickPhoto?: () => Promise<{ base64: string; mimeType: string }>
      pickPhotos?: (options?: { limit?: number }) => Promise<{ images: { base64: string; mimeType: string }[] }>
      startSpeechRecognition?: () => Promise<{ transcript: string }>
      stopSpeechRecognition?: () => Promise<{ transcript?: string }>
      storeRefreshToken?: (refreshToken: string) => Promise<void>
      loadRefreshToken?: () => Promise<{ refreshToken: string | null }>
      clearRefreshToken?: () => Promise<void>
    }
  }
}

/**
 * Returns true when running inside the native iOS GlowPic app (WKWebView).
 * Detection uses two signals:
 * 1. window.__NATIVE_BRIDGE__ injected by the native layer
 * 2. Custom user-agent string set by the native app
 */
export function isNativeApp(): boolean {
  if (typeof window === 'undefined') return false
  if (window.__NATIVE_BRIDGE__ !== undefined) return true
  if (typeof navigator !== 'undefined' && navigator.userAgent.includes('GlowPicApp')) return true
  return false
}

/**
 * Returns true only when running inside the native iOS app where Apple IAP is available.
 * Web always returns false — the caller should show a "Download App" CTA instead.
 */
export function canUsePay(): boolean {
  return isNativeApp()
}

import { Capacitor, registerPlugin } from '@capacitor/core'
import type { PluginListenerHandle } from '@capacitor/core'

interface NativeBridgePlugin {
  purchase(options: {
    productId: string
    appAccountToken: string
    expectedProductType?: StoreProductType
  }): Promise<NativePurchaseResult>
  finishTransaction(options: { transactionId: string }): Promise<void>
  getUnfinishedTransactions(): Promise<{
    transactions: Array<{
      receipt_data: string
      transaction_id: string
      product_id: string
      app_account_token?: string
    }>
  }>
  restorePurchases(options?: { syncAppStore?: boolean }): Promise<{
    transactions: NativeIapTransaction[]
    unverified_count: number
  }>
  addListener(
    eventName: 'appleIapTransactionUpdated',
    listener: (transaction: NativeIapTransaction) => void,
  ): Promise<PluginListenerHandle>
  getStoreProducts(options: { productIds: string[] }): Promise<{
    products: StoreProduct[]
  }>
  getStorefront(): Promise<{ countryCode: string }>
  manageSubscriptions(): Promise<{
    status: 'presented' | 'unavailable' | 'error'
    message?: string
  }>
  saveImage(options: { base64: string }): Promise<void>
  saveImageFromUrl(options: { url: string }): Promise<NativeImageSaveResult | void>
  pickPhoto(): Promise<{ base64: string; mimeType: string }>
  pickPhotos(options: { limit: number }): Promise<{ images: { base64: string; mimeType: string }[] }>
  startSpeechRecognition(): Promise<{ transcript: string }>
  stopSpeechRecognition(): Promise<{ transcript?: string }>
  storeRefreshToken(options: { refreshToken: string }): Promise<void>
  loadRefreshToken(): Promise<{ refreshToken: string | null }>
  clearRefreshToken(): Promise<void>
}

/**
 * Capacitor 原生环境初始化：将 Capacitor plugin 挂载到 window bridge，
 * 保持 membership/page.tsx 中的 window.__NATIVE_BRIDGE__ 调用代码零改动。
 */
export function initNativeBridge(): void {
  if (!Capacitor.isNativePlatform() || Capacitor.getPlatform() !== 'ios') return

  const NativeBridgePluginInstance = registerPlugin<NativeBridgePlugin>('NativeBridge')
  window.__NATIVE_BRIDGE__ = {
    purchase: (
      productId: string,
      appAccountToken: string,
      expectedProductType?: StoreProductType,
    ) =>
      NativeBridgePluginInstance.purchase({
        productId,
        appAccountToken,
        expectedProductType,
      }),
    finishTransaction: (transactionId: string) =>
      NativeBridgePluginInstance.finishTransaction({ transactionId }),
    getUnfinishedTransactions: () =>
      NativeBridgePluginInstance.getUnfinishedTransactions(),
    restorePurchases: (syncAppStore = false) =>
      NativeBridgePluginInstance.restorePurchases({ syncAppStore }),
    addAppleIapTransactionListener: async (listener) => {
      const handle = await NativeBridgePluginInstance.addListener(
        'appleIapTransactionUpdated',
        listener,
      )
      return () => handle.remove()
    },
    getStoreProducts: (productIds: string[]) =>
      NativeBridgePluginInstance.getStoreProducts({ productIds }),
    getStorefront: () =>
      NativeBridgePluginInstance.getStorefront(),
    manageSubscriptions: () =>
      NativeBridgePluginInstance.manageSubscriptions(),
    saveImage: (base64: string) =>
      NativeBridgePluginInstance.saveImage({ base64 }),
    saveImageFromUrl: (url: string) =>
      NativeBridgePluginInstance.saveImageFromUrl({ url }),
    pickPhoto: () =>
      NativeBridgePluginInstance.pickPhoto(),
    pickPhotos: (options) =>
      NativeBridgePluginInstance.pickPhotos({ limit: options?.limit ?? 5 }),
    startSpeechRecognition: () =>
      NativeBridgePluginInstance.startSpeechRecognition(),
    stopSpeechRecognition: () =>
      NativeBridgePluginInstance.stopSpeechRecognition(),
    storeRefreshToken: (refreshToken: string) =>
      NativeBridgePluginInstance.storeRefreshToken({ refreshToken }),
    loadRefreshToken: () =>
      NativeBridgePluginInstance.loadRefreshToken(),
    clearRefreshToken: () =>
      NativeBridgePluginInstance.clearRefreshToken(),
  }

}
